import { useEffect, useState } from "react";
import { Loader2, Save } from "lucide-react";
import StaffHeader from "@/components/StaffHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";

const MODES = [
  { value: "auto", label: "Auto", hint: "Follows the campaign dates automatically" },
  { value: "force_on", label: "Force On", hint: "Diwali surfaces show right now, regardless of dates" },
  { value: "force_off", label: "Force Off", hint: "Diwali surfaces are hidden, regardless of dates" },
];

export default function CampaignSettings() {
  const [settings, setSettings] = useState(null);
  const [mode, setMode] = useState("auto");
  const [milestones, setMilestones] = useState(["", "", ""]);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const load = async () => {
    const { data } = await api.get("/admin/campaigns/diwali");
    setSettings(data);
    setMode(data.mode);
    setMilestones(data.milestones.map(String));
  };

  useEffect(() => {
    load();
  }, []);

  const handleSave = async () => {
    const parsedMilestones = milestones.map((m) => parseInt(m, 10));
    if (parsedMilestones.some((m) => !m || m <= 0)) {
      toast({ variant: "destructive", title: "Milestones must be positive numbers" });
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put("/admin/campaigns/diwali", { mode, milestones: parsedMilestones });
      setSettings(data);
      toast({ title: "Campaign settings saved" });
      load();
    } catch (err) {
      toast({ variant: "destructive", title: "Could not save", description: formatApiErrorDetail(err.response?.data?.detail) });
    } finally {
      setSaving(false);
    }
  };

  if (!settings) {
    return (
      <div className="min-h-screen bg-amtm-bg">
        <StaffHeader />
        <div className="flex items-center justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-amtm-teal" /></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-amtm-bg" data-testid="campaign-settings-page">
      <StaffHeader />
      <div className="max-w-2xl mx-auto px-6 py-10">
        <h1 className="font-display text-2xl text-amtm-text mb-1">Diwali 2026 Campaign</h1>
        <p className="text-sm text-amtm-textMuted mb-6">
          Window: 30 Sep 2026 to 8 Nov 2026 (Asia/Kolkata). Controls /diwali, the homepage Diwali hero, and the site banner.
        </p>

        <div className="bg-amtm-surface border border-amtm-border rounded-xl p-5 mb-6 flex items-center justify-between" data-testid="campaign-settings-status">
          <div>
            <p className="text-sm text-amtm-textMuted">Currently</p>
            <p className={`font-semibold ${settings.active ? "text-amtm-teal" : "text-amtm-textMuted"}`} data-testid="campaign-settings-active-label">
              {settings.active ? "Active" : "Not active"}
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm text-amtm-textMuted">Raised so far</p>
            <p className="font-semibold text-amtm-text" data-testid="campaign-settings-raised">
              ₹{settings.raised.toLocaleString("en-IN")} &middot; {settings.supporters} supporters
            </p>
          </div>
        </div>

        <div className="mb-6">
          <Label className="mb-2 block">Mode</Label>
          <div className="grid gap-2.5">
            {MODES.map((m) => (
              <button
                key={m.value}
                type="button"
                onClick={() => setMode(m.value)}
                data-testid={`campaign-mode-${m.value}`}
                className={`text-left rounded-xl border px-4 py-3 transition-colors ${
                  mode === m.value ? "border-amtm-teal bg-amtm-teal/[0.06] ring-1 ring-amtm-teal" : "border-amtm-border hover:border-amtm-teal/50"
                }`}
              >
                <p className="font-semibold text-amtm-text">{m.label}</p>
                <p className="text-xs text-amtm-textMuted">{m.hint}</p>
              </button>
            ))}
          </div>
        </div>

        <div className="mb-6">
          <Label className="mb-2 block">Milestones (₹)</Label>
          <div className="grid grid-cols-3 gap-3">
            {milestones.map((m, i) => (
              <Input
                key={i}
                type="number"
                value={m}
                onChange={(e) => setMilestones((prev) => prev.map((v, idx) => (idx === i ? e.target.value : v)))}
                data-testid={`campaign-milestone-${i}`}
              />
            ))}
          </div>
        </div>

        <Button onClick={handleSave} disabled={saving} data-testid="campaign-settings-save-button" className="bg-amtm-teal text-white hover:bg-amtm-tealHover">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
          Save
        </Button>
      </div>
    </div>
  );
}
